import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Rocket, Calendar, Play, Activity, Flag,
  Target, DollarSign, Users, AlertTriangle, MessageCircle,
  ChevronRight, X, ArrowRight
} from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import useInView from '../components/useInView';

const stages = (t) => [
  {
    id: 1,
    icon: <Rocket size={28} />,
    color: '#2E5AFF',
    glow: 'rgba(46,90,255,0.4)',
    bgGlow: 'rgba(46,90,255,0.08)',
    ref: t('map_stage1_ref'),
    title: t('map_stage1'),
    desc: t('map_stage1_desc'),
    outputs: t('map_stage1_out'),
    substages: null,
    section: '/program',
    x: 10,
    y: 50,
  },
  {
    id: 2,
    icon: <Calendar size={28} />,
    color: '#8A2BE2',
    glow: 'rgba(138,43,226,0.4)',
    bgGlow: 'rgba(138,43,226,0.08)',
    ref: t('map_stage2_ref'),
    title: t('map_stage2'),
    desc: t('map_stage2_desc'),
    outputs: t('map_stage2_out'),
    substages: [
      { icon: <Target size={16} />, label: t('map_sub21') },
      { icon: <Calendar size={16} />, label: t('map_sub22') },
      { icon: <DollarSign size={16} />, label: t('map_sub23') },
      { icon: <Users size={16} />, label: t('map_sub24') },
      { icon: <AlertTriangle size={16} />, label: t('map_sub25') },
      { icon: <MessageCircle size={16} />, label: t('map_sub26') },
    ],
    section: '/program',
    x: 30,
    y: 25,
  },
  {
    id: 3,
    icon: <Play size={28} />,
    color: '#FF3B9A',
    glow: 'rgba(255,59,154,0.4)',
    bgGlow: 'rgba(255,59,154,0.08)',
    ref: t('map_stage3_ref'),
    title: t('map_stage3'),
    desc: t('map_stage3_desc'),
    outputs: t('map_stage3_out'),
    substages: null,
    section: '/program',
    x: 53,
    y: 50,
  },
  {
    id: 4,
    icon: <Activity size={28} />,
    color: '#5B8AFF',
    glow: 'rgba(91,138,255,0.4)',
    bgGlow: 'rgba(91,138,255,0.08)',
    ref: t('map_stage4_ref'),
    title: t('map_stage4'),
    desc: t('map_stage4_desc'),
    outputs: t('map_stage4_out'),
    substages: null,
    section: '/program',
    x: 72,
    y: 25,
  },
  {
    id: 5,
    icon: <Flag size={28} />,
    color: '#C050FF',
    glow: 'rgba(192,80,255,0.4)',
    bgGlow: 'rgba(192,80,255,0.08)',
    ref: t('map_stage5_ref'),
    title: t('map_stage5'),
    desc: t('map_stage5_desc'),
    outputs: t('map_stage5_out'),
    substages: null,
    section: '/program',
    x: 88,
    y: 50,
  },
];

// Mobile vertical list stage
const MobileStage = ({ stage, t, index }) => {
  const [open, setOpen] = useState(false);
  const [ref, inView] = useInView();

  return (
    <div
      ref={ref}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateX(0)' : 'translateX(-30px)',
        transition: `all 0.6s ease ${index * 0.15}s`,
      }}
    >
      <div
        onClick={() => setOpen(!open)}
        style={{
          background: 'rgba(18,22,36,0.8)',
          backdropFilter: 'blur(16px)',
          border: `1px solid ${open ? stage.color + '60' : 'rgba(255,255,255,0.06)'}`,
          borderRadius: '16px',
          padding: '20px',
          cursor: 'pointer',
          marginBottom: '12px',
          transition: 'all 0.3s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '52px',
              height: '52px',
              borderRadius: '14px',
              background: stage.bgGlow,
              border: `1px solid ${stage.color}40`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: stage.color,
              flexShrink: 0,
            }}>
              {stage.icon}
            </div>
            <div>
              <div style={{ fontSize: '11px', color: stage.color, fontWeight: 600, letterSpacing: '1px', marginBottom: '4px' }}>
                {stage.ref}
              </div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#fff' }}>{stage.title}</div>
            </div>
          </div>
          <ChevronRight
            size={20}
            style={{
              color: 'rgba(255,255,255,0.3)',
              transform: open ? 'rotate(90deg)' : 'rotate(0)',
              transition: 'transform 0.3s ease',
              flexShrink: 0,
            }}
          />
        </div>

        {open && (
          <div style={{
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            animation: 'fadeInUp 0.3s ease',
          }}>
            <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, marginBottom: '12px' }}>
              {stage.desc}
            </p>
            <div style={{
              background: `${stage.color}10`,
              borderRadius: '10px',
              padding: '12px',
              marginBottom: stage.substages ? '12px' : 0,
            }}>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', fontWeight: 600 }}>
                {t('map_outputs')}
              </div>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)' }}>{stage.outputs}</div>
            </div>
            {stage.substages && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontWeight: 600, marginBottom: '4px' }}>
                  {t('map_substages')}
                </div>
                {stage.substages.map((sub, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '8px 12px',
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: '8px',
                  }}>
                    <span style={{ color: stage.color }}>{sub.icon}</span>
                    <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.65)' }}>{sub.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ProjectMapPage = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [activeStage, setActiveStage] = useState(null);
  const [headerRef, headerInView] = useInView();
  const [mapRef, mapInView] = useInView(0.05);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const stagesData = stages(t);

  // SVG path for connecting nodes — curved line through all 5 points
  // Points in % of container width/height
  const nodePositions = stagesData.map(s => ({ x: s.x, y: s.y }));

  return (
    <div style={{ minHeight: '100vh', paddingTop: '80px', paddingBottom: '80px' }}>
      {/* Header */}
      <div
        ref={headerRef}
        style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '60px 24px 40px',
          textAlign: 'center',
          opacity: headerInView ? 1 : 0,
          transform: headerInView ? 'translateY(0)' : 'translateY(40px)',
          transition: 'all 0.8s ease',
        }}
      >
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 20px',
          borderRadius: '50px',
          background: 'rgba(138,43,226,0.12)',
          border: '1px solid rgba(138,43,226,0.3)',
          fontSize: '12px',
          fontWeight: 700,
          color: '#a855f7',
          letterSpacing: '2px',
          marginBottom: '24px',
          textTransform: 'uppercase',
        }}>
          ГОСТ Р 54869-2011
        </div>
        <h1 style={{
          fontSize: 'clamp(28px, 5vw, 52px)',
          fontWeight: 900,
          letterSpacing: '-1.5px',
          lineHeight: 1.1,
          marginBottom: '16px',
          color: '#fff',
        }}>
          {t('map_title').split(' ').slice(0, 2).join(' ')}{' '}
          <span style={{
            background: 'linear-gradient(135deg, #2E5AFF, #8A2BE2, #FF3B9A)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>{t('map_title').split(' ').slice(2).join(' ')}</span>
        </h1>
        <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.55)', maxWidth: '560px', margin: '0 auto' }}>
          {t('map_sub')}
        </p>
      </div>

      {/* MOBILE: vertical list */}
      {isMobile ? (
        <div style={{ maxWidth: '640px', margin: '0 auto', padding: '0 16px' }}>
          {stagesData.map((stage, i) => (
            <MobileStage key={stage.id} stage={stage} t={t} index={i} />
          ))}
        </div>
      ) : (
        /* DESKTOP: interactive map */
        <div
          ref={mapRef}
          style={{
            maxWidth: '1280px',
            margin: '0 auto',
            padding: '0 24px 40px',
            position: 'relative',
            opacity: mapInView ? 1 : 0,
            transition: 'opacity 0.8s ease',
          }}
        >
          {/* Map container */}
          <div style={{
            position: 'relative',
            width: '100%',
            paddingBottom: '45%',
            background: 'rgba(10,10,20,0.6)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '24px',
            overflow: 'hidden',
          }}>
            {/* SVG layer — connections */}
            <svg
              viewBox="0 0 1000 450"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
              }}
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient id="line-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#2E5AFF" />
                  <stop offset="50%" stopColor="#8A2BE2" />
                  <stop offset="100%" stopColor="#FF3B9A" />
                </linearGradient>
                <filter id="glow-filter">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {/* Background orbit rings */}
              <ellipse cx="500" cy="225" rx="460" ry="180" stroke="rgba(46,90,255,0.08)" strokeWidth="1" fill="none" />
              <ellipse cx="500" cy="225" rx="350" ry="140" stroke="rgba(138,43,226,0.06)" strokeWidth="1" fill="none" />

              {/* Animated flow path */}
              <path
                d={`M ${stagesData[0].x * 10} ${stagesData[0].y * 4.5}
                    C ${stagesData[0].x * 10 + 60} ${stagesData[1].y * 4.5},
                      ${stagesData[1].x * 10 - 30} ${stagesData[1].y * 4.5},
                      ${stagesData[1].x * 10} ${stagesData[1].y * 4.5}
                    C ${stagesData[1].x * 10 + 80} ${stagesData[2].y * 4.5},
                      ${stagesData[2].x * 10 - 80} ${stagesData[2].y * 4.5},
                      ${stagesData[2].x * 10} ${stagesData[2].y * 4.5}
                    C ${stagesData[2].x * 10 + 60} ${stagesData[3].y * 4.5},
                      ${stagesData[3].x * 10 - 30} ${stagesData[3].y * 4.5},
                      ${stagesData[3].x * 10} ${stagesData[3].y * 4.5}
                    C ${stagesData[3].x * 10 + 80} ${stagesData[4].y * 4.5},
                      ${stagesData[4].x * 10 - 80} ${stagesData[4].y * 4.5},
                      ${stagesData[4].x * 10} ${stagesData[4].y * 4.5}`}
                fill="none"
                stroke="url(#line-grad)"
                strokeWidth="2.5"
                strokeDasharray="12 6"
                filter="url(#glow-filter)"
                opacity="0.7"
              >
                <animate attributeName="stroke-dashoffset" from="0" to="-18" dur="1.2s" repeatCount="indefinite" />
              </path>

              {/* Static glow path behind */}
              <path
                d={`M ${stagesData[0].x * 10} ${stagesData[0].y * 4.5}
                    C ${stagesData[0].x * 10 + 60} ${stagesData[1].y * 4.5},
                      ${stagesData[1].x * 10 - 30} ${stagesData[1].y * 4.5},
                      ${stagesData[1].x * 10} ${stagesData[1].y * 4.5}
                    C ${stagesData[1].x * 10 + 80} ${stagesData[2].y * 4.5},
                      ${stagesData[2].x * 10 - 80} ${stagesData[2].y * 4.5},
                      ${stagesData[2].x * 10} ${stagesData[2].y * 4.5}
                    C ${stagesData[2].x * 10 + 60} ${stagesData[3].y * 4.5},
                      ${stagesData[3].x * 10 - 30} ${stagesData[3].y * 4.5},
                      ${stagesData[3].x * 10} ${stagesData[3].y * 4.5}
                    C ${stagesData[3].x * 10 + 80} ${stagesData[4].y * 4.5},
                      ${stagesData[4].x * 10 - 80} ${stagesData[4].y * 4.5},
                      ${stagesData[4].x * 10} ${stagesData[4].y * 4.5}`}
                fill="none"
                stroke="url(#line-grad)"
                strokeWidth="8"
                opacity="0.07"
              />
            </svg>

            {/* Stage nodes */}
            {stagesData.map((stage, i) => (
              <div
                key={stage.id}
                onClick={() => setActiveStage(activeStage?.id === stage.id ? null : stage)}
                style={{
                  position: 'absolute',
                  left: `${stage.x}%`,
                  top: `${stage.y}%`,
                  transform: 'translate(-50%, -50%)',
                  cursor: 'pointer',
                  zIndex: activeStage?.id === stage.id ? 20 : 10,
                  opacity: mapInView ? 1 : 0,
                  transition: `opacity 0.6s ease ${0.2 + i * 0.15}s`,
                }}
              >
                {/* Pulse rings */}
                {activeStage?.id === stage.id && (
                  <>
                    <div style={{
                      position: 'absolute',
                      top: '50%', left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '120px', height: '120px',
                      borderRadius: '50%',
                      border: `2px solid ${stage.color}`,
                      opacity: 0,
                      animation: 'node-pulse 1.5s ease-out infinite',
                    }} />
                    <div style={{
                      position: 'absolute',
                      top: '50%', left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '90px', height: '90px',
                      borderRadius: '50%',
                      border: `2px solid ${stage.color}`,
                      opacity: 0,
                      animation: 'node-pulse 1.5s ease-out infinite 0.4s',
                    }} />
                  </>
                )}

                {/* Node button */}
                <div
                  style={{
                    width: '72px',
                    height: '72px',
                    borderRadius: '50%',
                    background: activeStage?.id === stage.id
                      ? `radial-gradient(circle, ${stage.color}, ${stage.color}88)`
                      : `radial-gradient(circle, ${stage.color}33, ${stage.color}11)`,
                    border: `2px solid ${stage.color}${activeStage?.id === stage.id ? 'ff' : '80'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: activeStage?.id === stage.id ? '#fff' : stage.color,
                    boxShadow: activeStage?.id === stage.id
                      ? `0 0 30px ${stage.glow}, 0 0 60px ${stage.glow}`
                      : `0 0 15px ${stage.glow}40`,
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  {stage.icon}
                </div>

                {/* Stage label */}
                <div style={{
                  position: 'absolute',
                  bottom: '-40px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  textAlign: 'center',
                  width: '120px',
                }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
                    {stage.title}
                  </div>
                  <div style={{ fontSize: '10px', color: stage.color, fontWeight: 500 }}>п. {stage.ref.split(' ')[0]}</div>
                </div>

                {/* Hover tooltip hint */}
                <div style={{
                  position: 'absolute',
                  top: '-28px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontSize: '10px',
                  color: 'rgba(255,255,255,0.35)',
                  whiteSpace: 'nowrap',
                }}>
                  {t('map_click_detail')}
                </div>
              </div>
            ))}

            {/* Background decoration */}
            {[
              { x: '20%', y: '80%', r: 150, c: 'rgba(46,90,255,0.05)' },
              { x: '75%', y: '15%', r: 100, c: 'rgba(255,59,154,0.05)' },
            ].map((dec, i) => (
              <div key={i} style={{
                position: 'absolute',
                left: dec.x, top: dec.y,
                width: dec.r * 2, height: dec.r * 2,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${dec.c}, transparent)`,
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }} />
            ))}
          </div>

          {/* Detail panel */}
          {activeStage && (
            <div
              style={{
                marginTop: '24px',
                background: 'rgba(18,22,36,0.85)',
                backdropFilter: 'blur(20px)',
                border: `1px solid ${activeStage.color}40`,
                borderRadius: '20px',
                padding: '32px',
                animation: 'fadeInUp 0.4s ease',
                position: 'relative',
              }}
            >
              <button
                onClick={() => setActiveStage(null)}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background: 'rgba(255,255,255,0.06)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px',
                  cursor: 'pointer',
                  color: 'rgba(255,255,255,0.5)',
                }}
              >
                <X size={16} />
              </button>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
                <div>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '6px 14px',
                    borderRadius: '50px',
                    background: `${activeStage.color}18`,
                    border: `1px solid ${activeStage.color}40`,
                    fontSize: '11px',
                    fontWeight: 700,
                    color: activeStage.color,
                    marginBottom: '16px',
                  }}>
                    {activeStage.ref}
                  </div>
                  <h3 style={{ fontSize: '24px', fontWeight: 800, color: '#fff', marginBottom: '12px' }}>
                    {activeStage.title}
                  </h3>
                  <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.8, marginBottom: '20px' }}>
                    {activeStage.desc}
                  </p>
                  <div style={{
                    background: `${activeStage.color}10`,
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '20px',
                  }}>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontWeight: 700, letterSpacing: '1px', marginBottom: '8px' }}>
                      {t('map_outputs').toUpperCase()}
                    </div>
                    <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.7 }}>
                      {activeStage.outputs}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(activeStage.section)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 24px',
                      background: `linear-gradient(135deg, ${activeStage.color}, ${activeStage.color}aa)`,
                      color: '#fff',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '13px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {t('map_go_section')}
                    <ArrowRight size={14} />
                  </button>
                </div>

                {activeStage.substages && (
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: '2px', marginBottom: '16px', textTransform: 'uppercase' }}>
                      {t('map_substages')}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {activeStage.substages.map((sub, i) => (
                        <div
                          key={i}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px 16px',
                            background: 'rgba(255,255,255,0.03)',
                            border: '1px solid rgba(255,255,255,0.05)',
                            borderRadius: '10px',
                            animation: `fadeInUp 0.4s ease ${i * 0.06}s both`,
                          }}
                        >
                          <div style={{
                            width: '32px', height: '32px',
                            borderRadius: '8px',
                            background: `${activeStage.color}18`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: activeStage.color, flexShrink: 0,
                          }}>
                            {sub.icon}
                          </div>
                          <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)' }}>{sub.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* GOST Reference Bar */}
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        padding: '0 24px',
      }}>
        <div style={{
          background: 'rgba(18,22,36,0.6)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '16px',
          padding: '24px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          {stagesData.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '8px', height: '8px',
                borderRadius: '50%',
                background: s.color,
                boxShadow: `0 0 8px ${s.color}`,
              }} />
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                <span style={{ color: s.color, fontWeight: 600 }}>{s.title}</span>
                {' · '}
                {s.ref}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProjectMapPage;
